/**
 * AdminContentPage — content management for Shubh Time ops team.
 *
 * Tabs:
 *  1. Remedy Library   — CRUD for remedy entries
 *  2. Event Weights    — per-step weight sliders for each muhurta event category
 *  3. Nakshatra Tables — quality ratings per nakshatra per event type
 */
import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type AdminRemedy, type EventWeight, type NakshatraQuality } from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Plus, Pencil, Trash2, Save, X,
} from 'lucide-react'
import { SkeletonCard, SkeletonText } from '@/components/ui'

// ── Tab strip ─────────────────────────────────────────────────────────────────

type Tab = 'remedies' | 'weights' | 'nakshatra'

const TABS: { id: Tab; label: string }[] = [
  { id: 'remedies',  label: 'Remedy Library' },
  { id: 'weights',   label: 'Event Weights' },
  { id: 'nakshatra', label: 'Nakshatra Tables' },
]

const EVENT_TYPES = ['Marriage', 'Career', 'Business', 'Travel', 'Health', 'Education']
const QUALITY_VALUES = ['excellent', 'good', 'neutral', 'avoid'] as const
type Quality = typeof QUALITY_VALUES[number]

// ── Remedy CRUD ───────────────────────────────────────────────────────────────

type RemedyForm = Omit<AdminRemedy, 'id'>

const BLANK_FORM: RemedyForm = {
  title: '', category: 'gemstone', planet: '', priority: 'medium', description: '',
}

function RemedyRow({
  remedy,
  onEdit,
  onDelete,
}: {
  remedy: AdminRemedy
  onEdit: (r: AdminRemedy) => void
  onDelete: (id: string) => void
}) {
  const priorityColor = { high: 'text-inauspicious', medium: 'text-gold', low: 'text-star-dim' }
  return (
    <tr className="border-b border-cosmic-mist/50 hover:bg-cosmic-dusk/20 transition-colors">
      <td className="px-4 py-3 text-star-bright text-xs font-medium">{remedy.title}</td>
      <td className="px-4 py-3 text-star-dim text-xs font-mono">{remedy.category}</td>
      <td className="px-4 py-3 text-star-dim text-xs font-mono">{remedy.planet}</td>
      <td className={cn('px-4 py-3 text-xs font-medium', priorityColor[remedy.priority])}>{remedy.priority}</td>
      <td className="px-4 py-3 text-star-dim text-xs max-w-xs truncate">{remedy.description}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(remedy)}
            className="p-1.5 rounded hover:bg-cosmic-dusk text-star-dim hover:text-star-bright transition-colors"
            aria-label="Edit"
          >
            <Pencil size={12} />
          </button>
          <button
            onClick={() => onDelete(remedy.id)}
            className="p-1.5 rounded hover:bg-inauspicious/10 text-star-dim hover:text-inauspicious transition-colors"
            aria-label="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </td>
    </tr>
  )
}

function RemedyEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: AdminRemedy | null
  onSave: (form: RemedyForm, id?: string) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState<RemedyForm>(
    initial ? { title: initial.title, category: initial.category, planet: initial.planet, priority: initial.priority, description: initial.description }
             : BLANK_FORM,
  )
  const set = (k: keyof RemedyForm, v: string) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="bg-cosmic-void border border-violet-dark/40 rounded-xl p-4 space-y-3 mb-4">
      <p className="text-xs font-medium text-star-bright">{initial ? 'Edit Remedy' : 'New Remedy'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-2xs font-mono text-star-dim uppercase tracking-wider mb-1">Title</label>
          <input value={form.title} onChange={e => set('title', e.target.value)}
            className="input-cosmic w-full" placeholder="Remedy name" />
        </div>
        <div>
          <label className="block text-2xs font-mono text-star-dim uppercase tracking-wider mb-1">Planet</label>
          <input value={form.planet} onChange={e => set('planet', e.target.value)}
            className="input-cosmic w-full" placeholder="Jupiter, Saturn…" />
        </div>
        <div>
          <label className="block text-2xs font-mono text-star-dim uppercase tracking-wider mb-1">Category</label>
          <select value={form.category} onChange={e => set('category', e.target.value as any)}
            className="input-cosmic w-full">
            {['gemstone','mantra','yantra','ritual','charity'].map(c =>
              <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-2xs font-mono text-star-dim uppercase tracking-wider mb-1">Priority</label>
          <select value={form.priority} onChange={e => set('priority', e.target.value as any)}
            className="input-cosmic w-full">
            {['high','medium','low'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-2xs font-mono text-star-dim uppercase tracking-wider mb-1">Description</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)}
          rows={2} className="input-cosmic w-full resize-none" />
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onSave(form, initial?.id)}
          disabled={!form.title.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-dark text-star-bright hover:bg-violet-dark/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Save size={12} /> Save
        </button>
        <button onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-cosmic-mist text-star-dim hover:text-star-bright transition-colors">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  )
}

function RemediesTab() {
  const [editing, setEditing] = useState<AdminRemedy | 'new' | null>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'remedies'],
    queryFn: () => adminApi.getRemedies(),
    retry: 1,
  })
  const remedies = useMemo(() => data ?? [], [data])

  const saveMutation = useMutation({
    mutationFn: ({ form, id }: { form: RemedyForm; id?: string }) =>
      id ? adminApi.updateRemedy(id, form) : adminApi.createRemedy(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'remedies'] })
      setEditing(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteRemedy(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'remedies'] }),
  })

  if (isLoading) return <div className="p-4 space-y-3"><SkeletonCard /><SkeletonText lines={3} /></div>
  if (isError) return <div className="p-4 text-inauspicious text-sm">Failed to load remedies.</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setEditing('new')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-violet-dark/50 text-star-bright hover:bg-violet-dark/20 transition-colors"
        >
          <Plus size={13} /> Add Remedy
        </button>
      </div>

      {editing && (
        <RemedyEditor
          initial={editing === 'new' ? null : editing}
          onSave={(form, id) => saveMutation.mutate({ form, id })}
          onCancel={() => setEditing(null)}
        />
      )}

      <div className="card-cosmic overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-cosmic-mist">
                {['Title', 'Category', 'Planet', 'Priority', 'Description', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-2xs font-mono uppercase tracking-wider text-star-dim">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {remedies.map(r => (
                <RemedyRow
                  key={r.id}
                  remedy={r}
                  onEdit={r => setEditing(r)}
                  onDelete={id => deleteMutation.mutate(id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Event Weights ─────────────────────────────────────────────────────────────

const LAYER_LABELS: Record<string, string> = {
  gochar: 'Gochar', divisional: 'Divisional', dasha: 'Dasha',
  ashtakvarga: 'Ashtakvarga', nakshatra: 'Nakshatra', degrees: 'Degrees',
}

function WeightsTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'weights'],
    queryFn: () => adminApi.getEventWeights(),
    retry: 1,
  })
  const [weights, setWeights] = useState<EventWeight[]>([])
  const [saved, setSaved] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => { if (data) setWeights(data) }, [data])

  const saveMutation = useMutation({
    mutationFn: (w: EventWeight[]) => adminApi.saveEventWeights(w),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'weights'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  const layers = Object.keys(LAYER_LABELS)

  if (isLoading) return <div className="p-4 space-y-3"><SkeletonCard /><SkeletonText lines={3} /></div>
  if (isError) return <div className="p-4 text-inauspicious text-sm">Failed to load event weights.</div>

  return (
    <div className="space-y-4">
      <p className="text-xs text-star-muted">
        Each row must sum to 100. Weights determine how each scoring layer contributes to the muhurta score for a given event type.
      </p>

      <div className="card-cosmic overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-cosmic-mist">
                <th className="px-4 py-3 text-left text-2xs font-mono uppercase tracking-wider text-star-dim">Event Type</th>
                {layers.map(l => (
                  <th key={l} className="px-3 py-3 text-left text-2xs font-mono uppercase tracking-wider text-star-dim">
                    {LAYER_LABELS[l]}
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-2xs font-mono uppercase tracking-wider text-star-dim">Sum</th>
              </tr>
            </thead>
            <tbody>
              {weights.map((row, ri) => {
                const sum = layers.reduce((acc, l) => acc + (row.layers[l] ?? 0), 0)
                const valid = sum === 100
                return (
                  <tr key={row.eventType} className="border-b border-cosmic-mist/50">
                    <td className="px-4 py-2 text-star-bright font-medium">{row.eventType}</td>
                    {layers.map(l => (
                      <td key={l} className="px-3 py-2">
                        <input
                          type="number"
                          min={0} max={100}
                          value={row.layers[l] ?? 0}
                          onChange={e => {
                            const v = Math.max(0, Math.min(100, Number(e.target.value)))
                            setWeights(prev => prev.map((r, i) =>
                              i === ri ? { ...r, layers: { ...r.layers, [l]: v } } : r,
                            ))
                          }}
                          className="input-cosmic w-16 text-center"
                        />
                      </td>
                    ))}
                    <td className={cn('px-3 py-2 font-mono font-bold', valid ? 'text-auspicious' : 'text-inauspicious')}>
                      {sum}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => saveMutation.mutate(weights)}
          disabled={saveMutation.isPending}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-violet-dark text-star-bright hover:bg-violet-dark/80 disabled:opacity-50 transition-colors"
        >
          <Save size={12} /> {saveMutation.isPending ? 'Saving…' : 'Save Weights'}
        </button>
        {saved && <span className="text-xs text-auspicious">✓ Saved</span>}
      </div>
    </div>
  )
}

// ── Nakshatra Quality Table ───────────────────────────────────────────────────

const QUALITY_COLORS: Record<Quality, string> = {
  excellent: 'bg-auspicious/20 text-auspicious',
  good:      'bg-gold/15 text-gold',
  neutral:   'bg-star-dim/10 text-star-dim',
  avoid:     'bg-inauspicious/15 text-inauspicious',
}

function NakshatraTab() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'nakshatra'],
    queryFn: () => adminApi.getNakshatraTable(),
    retry: 1,
  })
  const [table, setTable] = useState<NakshatraQuality[]>([])
  const [saved, setSaved] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => { if (data) setTable(data) }, [data])

  const saveMutation = useMutation({
    mutationFn: (t: NakshatraQuality[]) => adminApi.saveNakshatraTable(t),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'nakshatra'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  function cycleQuality(ni: number, eventType: string) {
    setTable(prev => prev.map((row, i) => {
      if (i !== ni) return row
      const cur = row.qualities[eventType] as Quality
      const idx = QUALITY_VALUES.indexOf(cur)
      const next = QUALITY_VALUES[(idx + 1) % QUALITY_VALUES.length]
      return { ...row, qualities: { ...row.qualities, [eventType]: next } }
    }))
  }

  if (isLoading) return <div className="p-4 space-y-3"><SkeletonCard /><SkeletonText lines={3} /></div>
  if (isError) return <div className="p-4 text-inauspicious text-sm">Failed to load nakshatra table.</div>

  return (
    <div className="space-y-4">
      <p className="text-xs text-star-muted">Click a cell to cycle: excellent → good → neutral → avoid. Changes take effect within 5 minutes after saving.</p>

      <div className="card-cosmic overflow-auto max-h-[480px]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-cosmic-nebula z-10">
            <tr className="border-b border-cosmic-mist">
              <th className="px-3 py-2 text-left text-2xs font-mono text-star-dim sticky left-0 bg-cosmic-nebula">Nakshatra</th>
              {EVENT_TYPES.map(et => (
                <th key={et} className="px-3 py-2 text-center text-2xs font-mono text-star-dim whitespace-nowrap">{et}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.map((row, ni) => (
              <tr key={row.nakshatra} className="border-b border-cosmic-mist/30 hover:bg-cosmic-dusk/10">
                <td className="px-3 py-1.5 text-star font-mono text-2xs sticky left-0 bg-cosmic-void whitespace-nowrap">
                  {row.nakshatra}
                </td>
                {EVENT_TYPES.map(et => {
                  const q = row.qualities[et] as Quality
                  return (
                    <td key={et} className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => cycleQuality(ni, et)}
                        className={cn(
                          'px-2 py-0.5 rounded text-2xs font-medium cursor-pointer transition-colors hover:opacity-80',
                          QUALITY_COLORS[q],
                        )}
                      >
                        {q.charAt(0).toUpperCase() + q.slice(1, 4)}
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => saveMutation.mutate(table)}
          disabled={saveMutation.isPending}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-violet-dark text-star-bright hover:bg-violet-dark/80 disabled:opacity-50 transition-colors"
        >
          <Save size={12} /> {saveMutation.isPending ? 'Saving…' : 'Save Table'}
        </button>
        {saved && <span className="text-xs text-auspicious">✓ Saved</span>}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AdminContentPage() {
  const [activeTab, setActiveTab] = useState<Tab>('remedies')

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h2 className="text-lg font-bold text-star-bright">Content Management</h2>
        <p className="text-xs text-star-muted">Edit remedy library, event weights, and nakshatra quality tables</p>
      </div>

      {/* Tab strip */}
      <div className="flex gap-1 border-b border-cosmic-mist">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-4 py-2 text-xs font-medium border-b-2 transition-colors',
              activeTab === tab.id
                ? 'border-violet-dark text-star-bright'
                : 'border-transparent text-star-dim hover:text-star-bright',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'remedies'  && <RemediesTab />}
      {activeTab === 'weights'   && <WeightsTab />}
      {activeTab === 'nakshatra' && <NakshatraTab />}
    </div>
  )
}
